import React from 'react';
import { Empty, Table, Spin } from 'antd';
import { Column, Line, Pie, Scatter } from '@ant-design/charts';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import {
  BarChart,
  LineChart,
  PieChart,
  ScatterChart
} from 'echarts/charts';
import {
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

// Register ECharts components
echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  BarChart,
  LineChart,
  PieChart,
  ScatterChart,
  CanvasRenderer
]);

const ChartPreview = ({ type, data, config, loading }) => {
  if (loading) {
    return (
      <div style={{ height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!data || data.length === 0 || !config.xAxis) {
    return (
      <div style={{ height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Empty 
          description="Configure your chart by selecting fields for X-axis and Y-axis"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </div>
    );
  }

  const renderChart = () => {
    switch (type) {
      case 'bar':
        return renderBarChart();
      case 'line':
        return renderLineChart();
      case 'pie':
        return renderPieChart();
      case 'scatter':
        return renderScatterChart();
      case 'table':
        return renderTable();
      default:
        return <Empty description="Unsupported chart type" />;
    }
  };

  const renderBarChart = () => {
    if (!config.yAxis) return <Empty description="Select Y-axis field" />;

    const chartConfig = {
      data,
      xField: config.xAxis.name,
      yField: config.yAxis.name,
      colorField: config.color?.name,
      height: 400,
      label: {
        position: 'middle',
        style: {
          fill: '#FFFFFF',
          opacity: 0.6,
        },
      },
      meta: {
        [config.xAxis.name]: {
          alias: config.xAxis.name,
        },
        [config.yAxis.name]: {
          alias: config.yAxis.name,
        },
      },
    };

    return <Column {...chartConfig} />;
  };

  const renderLineChart = () => {
    if (!config.yAxis) return <Empty description="Select Y-axis field" />;

    const chartConfig = {
      data,
      xField: config.xAxis.name,
      yField: config.yAxis.name,
      seriesField: config.color?.name,
      height: 400,
      point: {
        size: 5,
        shape: 'diamond',
      },
      label: {},
      meta: {
        [config.xAxis.name]: {
          alias: config.xAxis.name,
        },
        [config.yAxis.name]: {
          alias: config.yAxis.name,
        },
      },
    };

    return <Line {...chartConfig} />;
  };

  const renderPieChart = () => {
    // Group data by x-axis field and sum y-axis values
    const groupedData = data.reduce((acc, item) => {
      const key = item[config.xAxis.name];
      const value = config.yAxis ? item[config.yAxis.name] : 1;
      
      if (!acc[key]) {
        acc[key] = 0;
      }
      acc[key] += value;
      return acc;
    }, {});

    const pieData = Object.entries(groupedData).map(([key, value]) => ({
      type: key,
      value: value
    }));

    const chartConfig = {
      data: pieData,
      angleField: 'value',
      colorField: 'type',
      radius: 0.8,
      height: 400,
      label: {
        type: 'outer',
        content: '{name} {percentage}',
      },
      interactions: [
        {
          type: 'pie-legend-active',
        },
        {
          type: 'element-active',
        },
      ],
    };

    return <Pie {...chartConfig} />;
  };

  const renderScatterChart = () => {
    if (!config.yAxis) return <Empty description="Select Y-axis field" />;

    const chartConfig = {
      data,
      xField: config.xAxis.name,
      yField: config.yAxis.name,
      colorField: config.color?.name,
      sizeField: config.size?.name,
      size: config.size ? [4, 30] : 8,
      height: 400,
      shape: 'circle',
      pointStyle: {
        fillOpacity: 0.8,
        stroke: '#bbb',
      },
      meta: {
        [config.xAxis.name]: {
          alias: config.xAxis.name,
        },
        [config.yAxis.name]: {
          alias: config.yAxis.name,
        },
      },
    };

    return <Scatter {...chartConfig} />;
  };

  const renderTable = () => {
    const columns = [
      {
        title: config.xAxis.name,
        dataIndex: config.xAxis.name,
        key: config.xAxis.name,
        sorter: (a, b) => {
          const aVal = a[config.xAxis.name];
          const bVal = b[config.xAxis.name];
          if (typeof aVal === 'string') {
            return aVal.localeCompare(bVal);
          }
          return aVal - bVal;
        },
      }
    ];

    if (config.yAxis) {
      columns.push({
        title: config.yAxis.name,
        dataIndex: config.yAxis.name,
        key: config.yAxis.name,
        sorter: (a, b) => a[config.yAxis.name] - b[config.yAxis.name],
        render: (value) => typeof value === 'number' ? value.toLocaleString() : value,
      });
    }

    if (config.color) {
      columns.push({
        title: config.color.name,
        dataIndex: config.color.name,
        key: config.color.name,
        filters: [...new Set(data.map(item => item[config.color.name]))].map(val => ({
          text: val,
          value: val,
        })),
        onFilter: (value, record) => record[config.color.name] === value,
      });
    }

    return (
      <Table
        dataSource={data}
        columns={columns}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
        }}
        scroll={{ y: 350 }}
        size="small"
        rowKey={(record, index) => index}
      />
    );
  };

  return (
    <div style={{ height: '100%', width: '100%' }}>
      {renderChart()}
    </div>
  );
};

export default ChartPreview;